import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';

// ⚠️ THIS PAGE IS FOR INTERNAL/ADMIN USE ONLY
// AI automation is our competitive advantage - not exposed to regular users
import { toast } from 'sonner';
import { 
  Zap, 
  Brain, 
  CheckCircle, 
  TrendingUp, 
  AlertCircle,
  Clock,
  DollarSign,
  Shield,
  MessageSquare,
  FileText,
  Activity,
  Play,
  RefreshCw,
  Settings,
  Building,
  Users,
  Wrench,
  Eye
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

export default function Automation() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [automationStatus, setAutomationStatus] = useState(null);
  const [complianceScan, setComplianceScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [runningTask, setRunningTask] = useState(null);

  // Check access permissions
  const hasAccess = user && (user.role === 'super_admin' || user.role === 'admin' || user.role === 'support_agent');

  const loadAutomationStatus = async () => {
    try {
      const { data } = await api.get('/automation/status');
      setAutomationStatus(data);
    } catch (error) {
      console.error('Failed to load automation status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasAccess) {
      loadAutomationStatus();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  // Show access denied for non-admin users
  if (!loading && user && !hasAccess) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="p-8 max-w-md text-center">
            <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Admin Access Required</h2>
            <p className="text-slate-600 mb-4">
              The AI Automation dashboard is only available to administrators. 
              Our AI agents work behind the scenes to automate your property management tasks.
            </p>
            <Button onClick={() => navigate('/dashboard')} variant="outline">
              Return to Dashboard
            </Button>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  const runComplianceScan = async () => {
    setRunningTask('compliance');
    try {
      const { data } = await api.get('/ai/compliance-scan');
      setComplianceScan(data);
      toast.success(`Compliance scan complete. Score: ${data.compliance_score}%`);
    } catch (error) {
      toast.error('Failed to run compliance scan');
    } finally {
      setRunningTask(null);
    }
  };

  const runDailyAutomation = async () => {
    setRunningTask('daily');
    try {
      const { data } = await api.post('/ai/run-automation');
      toast.success('Daily automation complete');
    } catch (error) {
      toast.error('Failed to run automation');
    } finally {
      setRunningTask(null);
    }
  };

  const triggerWorkflow = async (workflowName) => {
    setRunningTask(workflowName);
    try {
      await api.post('/automation/trigger-workflow', { workflow_name: workflowName, data: {} });
      toast.success(`Workflow "${workflowName}" triggered`);
    } catch (error) {
      toast.error('Failed to trigger workflow');
    } finally {
      setRunningTask(null);
    }
  };

  const automationFeatures = [
    {
      icon: Brain,
      title: "AI Tenant Screening",
      description: "Automatic application analysis with affordability scoring and risk assessment",
      color: "bg-purple-500",
      metrics: "Auto-approves 70% of applications",
      workflow: "application_screening"
    },
    {
      icon: MessageSquare,
      title: "Smart Enquiry Responses",
      description: "AI chatbot instantly responds to property enquiries 24/7",
      color: "bg-blue-500",
      metrics: "< 2 min response time",
      workflow: "enquiry_response"
    },
    {
      icon: TrendingUp,
      title: "Dynamic Rent Pricing",
      description: "Smart market analysis suggests optimal rent based on property features and local comparables",
      color: "bg-green-500",
      metrics: "Maximizes yield",
      workflow: "rent_optimization"
    },
    {
      icon: Shield,
      title: "Predictive Arrears Detection",
      description: "Machine learning predicts payment issues before they happen",
      color: "bg-red-500",
      metrics: "85% accuracy",
      workflow: "arrears_detection"
    },
    {
      icon: Zap,
      title: "Maintenance Auto-Triage",
      description: "AI categorizes urgency and auto-dispatches contractors for emergencies",
      color: "bg-yellow-500",
      metrics: "Instant emergency response",
      workflow: "maintenance_triage"
    },
    {
      icon: FileText,
      title: "DocuSign E-Signatures",
      description: "Automated tenancy agreements with legally-binding digital signatures",
      color: "bg-indigo-500",
      metrics: "100% digital",
      workflow: null
    },
    {
      icon: Clock,
      title: "Smart Payment Reminders",
      description: "WhatsApp & email reminders sent automatically before rent is due",
      color: "bg-pink-500",
      metrics: "Reduces late payments 40%",
      workflow: "rent_reminders"
    },
    {
      icon: AlertCircle,
      title: "Compliance Monitoring",
      description: "Automatic tracking of certificate expiry with proactive alerts",
      color: "bg-orange-500",
      metrics: "Zero missed deadlines",
      workflow: "compliance_check"
    },
    {
      icon: Activity,
      title: "Viewing Follow-ups",
      description: "Automated follow-up emails after property viewings",
      color: "bg-teal-500",
      metrics: "2x conversion rate",
      workflow: "viewing_followup"
    }
  ];

  return (
    <DashboardLayout>
      <div className="max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-display font-bold text-slate-900">AI Automation Center</h1>
                <p className="text-lg text-slate-600">Zero-overhead property management powered by AI</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button onClick={runComplianceScan} variant="outline" disabled={runningTask === 'compliance'}>
                <Shield className="w-4 h-4 mr-2" />
                {runningTask === 'compliance' ? 'Scanning...' : 'Run Compliance Scan'}
              </Button>
              <Button onClick={runDailyAutomation} disabled={runningTask === 'daily'}>
                <Play className="w-4 h-4 mr-2" />
                {runningTask === 'daily' ? 'Running...' : 'Run All Automation'}
              </Button>
            </div>
          </div>
        </div>

        {/* Status Banner */}
        {automationStatus && (
          <Card className="p-6 mb-8 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="font-semibold text-green-900">
                    {automationStatus.status === 'fully_autonomous' ? 'Full Service Active' : 'Active'}
                  </span>
                </div>
                <span className="text-slate-600">•</span>
                <span className="text-slate-700">
                  <strong>{automationStatus.scheduler?.active_tasks || 0}</strong> Background Tasks
                </span>
                <span className="text-slate-600">•</span>
                <span className="text-slate-700">
                  <strong>{automationStatus.active_workflows || 0}</strong> Active Workflows
                </span>
              </div>
              <Button size="sm" variant="outline" onClick={() => setShowDetails(true)}>
                <Eye className="w-4 h-4 mr-2" />
                View Details
              </Button>
            </div>
          </Card>
        )}

        {/* Compliance Scan Results */}
        {complianceScan && (
          <Card className={`p-6 mb-8 ${complianceScan.critical_issues > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Shield className={complianceScan.critical_issues > 0 ? 'text-red-600' : 'text-green-600'} />
                Compliance Scan Results
              </h3>
              <span className={`text-2xl font-bold ${complianceScan.critical_issues > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {complianceScan.compliance_score}%
              </span>
            </div>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-lg">
                <p className="text-sm text-slate-600">Properties Scanned</p>
                <p className="text-xl font-bold">{complianceScan.total_properties}</p>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <p className="text-sm text-slate-600">Critical Issues</p>
                <p className="text-xl font-bold text-red-600">{complianceScan.critical_issues}</p>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <p className="text-sm text-slate-600">Warnings</p>
                <p className="text-xl font-bold text-yellow-600">{complianceScan.warnings}</p>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <p className="text-sm text-slate-600">Status</p>
                <p className="text-xl font-bold capitalize">{complianceScan.status?.replace('_', ' ')}</p>
              </div>
            </div>
          </Card>
        )}

        {/* Key Metrics */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Brain className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-xs font-medium text-green-600 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> Active
              </span>
            </div>
            <h3 className="text-2xl font-bold text-slate-900">100%</h3>
            <p className="text-sm text-slate-600">Automation Rate</p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
              <span className="text-xs font-medium text-green-600 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> -95%
              </span>
            </div>
            <h3 className="text-2xl font-bold text-slate-900">£0</h3>
            <p className="text-sm text-slate-600">Manual Labor Cost</p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-xs font-medium text-green-600 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> +40%
              </span>
            </div>
            <h3 className="text-2xl font-bold text-slate-900">2 min</h3>
            <p className="text-sm text-slate-600">Avg Response Time</p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Activity className="w-6 h-6 text-orange-600" />
              </div>
              <span className="text-xs font-medium text-green-600 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> 24/7
              </span>
            </div>
            <h3 className="text-2xl font-bold text-slate-900">Always On</h3>
            <p className="text-sm text-slate-600">AI Monitoring</p>
          </Card>
        </div>

        {/* Automation Features Grid */}
        <div>
          <h2 className="text-2xl font-display font-bold mb-6">Automated Workflows</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {automationFeatures.map((feature, index) => {
              const Icon = feature.icon;
              const isRunning = runningTask === feature.workflow;
              return (
                <Card key={index} className="p-6 hover:shadow-xl transition-all group">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 ${feature.color} rounded-lg group-hover:scale-110 transition-transform`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    {feature.workflow && (
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => triggerWorkflow(feature.workflow)}
                        disabled={isRunning}
                      >
                        {isRunning ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                      </Button>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-slate-600 mb-3">{feature.description}</p>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                      {feature.metrics}
                    </span>
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </div>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Integration Status */}
        <Card className="p-8 mt-8 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <h2 className="text-2xl font-display font-bold mb-6">Live Integrations</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-white/10 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="font-semibold">OpenAI GPT-4o</span>
              </div>
              <p className="text-sm text-white/70">AI Analysis & Chat</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="font-semibold">GoCardless</span>
              </div>
              <p className="text-sm text-white/70">Direct Debit Payments</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="font-semibold">DocuSign</span>
              </div>
              <p className="text-sm text-white/70">E-Signatures</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="font-semibold">Twilio WhatsApp</span>
              </div>
              <p className="text-sm text-white/70">Instant Notifications</p>
            </div>
          </div>
        </Card>

        {/* Details Dialog */}
        <Dialog open={showDetails} onOpenChange={setShowDetails}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Automation System Details</DialogTitle>
            </DialogHeader>
            {automationStatus && (
              <div className="space-y-6 mt-4">
                <div>
                  <h4 className="font-semibold mb-3">Scheduler Status</h4>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-600">Status</p>
                        <p className="font-medium">{automationStatus.scheduler?.running ? 'Running' : 'Stopped'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Active Tasks</p>
                        <p className="font-medium">{automationStatus.scheduler?.active_tasks}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold mb-3">Active Modules</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {automationStatus.automation_modules && Object.entries(automationStatus.automation_modules).map(([key, value]) => (
                      <div key={key} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                        {value ? <CheckCircle className="w-4 h-4 text-green-600" /> : <AlertCircle className="w-4 h-4 text-red-600" />}
                        <span className="text-sm capitalize">{key.replace(/_/g, ' ')}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold mb-3">Registered Workflows</h4>
                  <div className="flex flex-wrap gap-2">
                    {automationStatus.workflows?.map((workflow, index) => (
                      <span key={index} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                        {workflow}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
